import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUserData } from '@/hooks/useUserData'
import { useCollections } from '@/hooks/useCollections'
import { Button } from '@/components/ui/button'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Camera, Plus, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react'
import { CollectionPreview } from '@/components/shared/CollectionPreview'
import { CreateCollectionSheet } from '@/components/shared/CreateCollectionSheet'
import { ProfileChatList } from '@/components/profile/ProfileChatList'
import { useAuthState } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth/AuthModal'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useNotificationPrompt } from '@/hooks/useNotificationPrompt'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'
import { cn } from '@/lib/utils'

/**
 * ProfilePage - Instant Loading ⚡
 * 
 * After the first visit, this page loads INSTANTLY from cache with zero delay.
 * The profile display data and chat list are cached to localStorage,
 * allowing the UI to render immediately while fresh data loads in the background.
 * 
 * Performance: First load ~2-3s, subsequent loads ~100-200ms
 */
export default function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const onProfileRoute = location.pathname === '/profile'
  const { userData, getCachedProfile, isArtist, loading: userDataLoading } = useUserData()
  const { collections, loading: collectionsLoading } = useCollections()
  const { user, loading: authLoading } = useAuthState()
  const { trackEvent } = useAnalytics()
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showLoggedOutPrompt, setShowLoggedOutPrompt] = useState(false)
  const [authModalMode, setAuthModalMode] = useState<'login' | 'signup'>('login')
  const [currentCollectionPage, setCurrentCollectionPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(3)
  
  // Prompt for notification permissions on page load
  useNotificationPrompt()
  
  // Get cached profile for instant initial render (no flicker!)
  const cachedProfile = getCachedProfile()
 

  // Redirect artists to feed only while viewing profile
  useEffect(() => {
    if (!onProfileRoute) return
    if (!userDataLoading && user && isArtist) {
      console.log('[ProfilePage] Artist user attempting to access profile page, redirecting to feed')
      navigate('/feed', { replace: true })
    }
  }, [onProfileRoute, user, isArtist, userDataLoading, navigate])

  useEffect(() => {
    if (location.pathname !== '/profile') {
      setShowLoginModal(false)
      setShowLoggedOutPrompt(false)
    }
  }, [location.pathname])

  useEffect(() => {
    if (!onProfileRoute || authLoading || user) {
      return
    }
    setShowLoggedOutPrompt(true)
  }, [onProfileRoute, authLoading, user])

  // Clear modal if user becomes authenticated
  useEffect(() => {
    if (user) {
      setShowLoginModal(false)
      setAuthModalMode('login')
    }
  }, [user])

  // Detect screen size for items per page (3 on mobile, 4 on desktop)
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      setItemsPerPage(isMobile ? 3 : 4)
      setCurrentCollectionPage(0) // Reset to first page on resize
    }

    handleResize() // Set initial value
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLoginModalOpenChange = (isOpen: boolean) => {
    setShowLoginModal(isOpen)
  }

  // Get user's display name with fallback to cache for instant UI on page load
  const displayName = userData?.instagram || cachedProfile?.instagram || userData?.email || cachedProfile?.email || ''
  
  // Get initials for avatar fallback with cache support
  const getInitials = () => {
    const instagram = userData?.instagram || cachedProfile?.instagram
    const email = userData?.email || cachedProfile?.email
    
    if (instagram) {
      return instagram.substring(0, 2).toUpperCase()
    } else if (email) {
      return email.substring(0, 2).toUpperCase()
    }
    return 'U'
  }
  
  // Get avatar URL with cache support for instant display
  const avatarUrl = (userData && 'avatarUrl' in userData && userData.avatarUrl) || 
                    (cachedProfile && 'avatarUrl' in cachedProfile && cachedProfile.avatarUrl) || 
                    null

  // Handle avatar image change
  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user?.uid) return

    try {
      setIsUploadingAvatar(true)
      
      // Upload using secure method to user-avatars folder
      const downloadURL = await uploadImageToStorageSecure(
        file,
        `user-avatars/${user.uid}`,
        user.uid
      )
      
      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        avatarUrl: downloadURL
      })
      
      console.log('Avatar updated successfully')
    } catch (error) {
      console.error('Avatar upload failed:', error)
      alert(`Avatar upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  // Handle clicking on a collection
  const handleCollectionClick = (collectionId: string) => {
    trackEvent('collection_clicked', { collection_id: collectionId })
    navigate(`/profile/collection/${collectionId}`)
  }

  // Calculate pagination for collections
  const totalCollectionSlots = collections.length + 1 // collections + new collection box
  const totalPages = Math.ceil(totalCollectionSlots / itemsPerPage)
  const hasMultiplePages = totalPages > 1
  
  const handlePreviousPage = () => {
    setCurrentCollectionPage((prev) => Math.max(0, prev - 1))
  }
  
  const handleNextPage = () => {
    setCurrentCollectionPage((prev) => Math.min(totalPages - 1, prev + 1))
  }

  // Get items for current page
  const getPageItems = () => {
    const startIndex = currentCollectionPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    
    // All items including collections and new collection box
    const allItems = [...collections, { id: 'new-collection-box', isNewBox: true }]
    const pageItems = allItems.slice(startIndex, endIndex)
    
    // Fill remaining slots with empty boxes
    const emptyBoxesNeeded = itemsPerPage - pageItems.length
    const emptyBoxes = Array.from({ length: emptyBoxesNeeded }, (_, i) => ({
      id: `empty-${i}`,
      isEmpty: true
    }))
    
    return [...pageItems, ...emptyBoxes]
  }

  if (authLoading && !user && !onProfileRoute) {
    return null
  }

  // Render skeleton loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background transition-colors pb-bottom-nav pt-safe-native-top md:pt-4">
        <div className="phone-container-padded">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-4">
            <div className="h-8 w-32 bg-muted rounded-lg animate-pulse" />
            <div className="h-10 w-10 bg-muted rounded-lg animate-pulse" />
          </div>

          {/* User info skeleton */}
          <div className="flex items-center">
            <div className="w-18 h-18 shrink-0 overflow-hidden bg-muted rounded-[var(--radius)] animate-pulse" />
            <div className="ml-3 flex-1">
              <div className="h-6 w-40 bg-muted rounded-lg animate-pulse mb-2" />
              <div className="h-4 w-48 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>

          {/* Chat section skeleton */}
          <div className="mt-2">
            <div className="h-6 w-32 bg-muted rounded-lg animate-pulse mb-4" />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          </div>

          {/* Collections section skeleton */}
          <div className="mt-4">
            <div className="h-6 w-32 bg-muted rounded-lg animate-pulse mb-4" />
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-muted rounded-lg animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Logged-out profile UI / AuthModal portals to document.body — must not mount off /profile (persistent shell).
  if (!user && !onProfileRoute) {
    return null
  }

  // Show auth modal if user is not logged in
  if (!user) {
    return (
      <>
        <div className="h-screen-mobile-minus-nav bg-background transition-colors">
          <div className="phone-container-padded flex h-full min-h-0 flex-col pt-safe-native-top-compact-md">
            <div className="flex items-center gap-3 pb-2 flex-shrink-0">
              <div className="w-18 h-18 shrink-0 rounded-[var(--radius)] bg-muted" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-5 w-36 rounded-md bg-muted" />
                <div className="h-4 w-48 rounded-md bg-muted" />
              </div>
            </div>

            <div className="flex-1 min-h-0 pb-4 space-y-3">
              <div className="h-5 w-24 rounded-md bg-muted" />
              <div className="space-y-2">
                {[1, 2, 3, 4].map((index) => (
                  <div key={index} className="h-16 rounded-lg bg-muted" />
                ))}
              </div>
            </div>

            <div className="flex-shrink-0 pb-4">
              <div className="h-5 w-28 rounded-md bg-muted mb-3" />
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="aspect-square rounded-lg bg-muted" />
                ))}
              </div>
            </div>
          </div>
        </div>

        <Dialog open={showLoggedOutPrompt && onProfileRoute} onOpenChange={setShowLoggedOutPrompt}>
          <DialogPortal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <DialogPrimitive.Content
              className={cn(
                'fixed left-0 right-0 top-[50%] z-[110] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto',
                'max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]',
              )}
            >
              <div className="flex items-center justify-between p-6 pb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  {t('navigation.profile')}
                </h2>
                <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none">
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              </div>

              <div className="px-6 pb-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('profile.loggedOutInvite')}
                </p>

                <div className="flex flex-col items-center gap-3 pt-6">
                  <Button
                    onClick={() => {
                      setShowLoggedOutPrompt(false)
                      setAuthModalMode('signup')
                      setShowLoginModal(true)
                    }}
                    className="w-full h-14 text-base font-medium rounded-[0.6rem]"
                  >
                    {t('auth.signup')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowLoggedOutPrompt(false)}
                    className="w-full h-14 text-base font-medium rounded-[0.6rem]"
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPortal>
        </Dialog>

        <AuthModal
          isOpen={showLoginModal && onProfileRoute}
          onOpenChange={handleLoginModalOpenChange}
          defaultMode={authModalMode}
          source="profile_page"
        />
      </>
    )
  }

  return (
    <>
    <div className="h-screen-mobile-minus-nav bg-background transition-colors">

      <div className="phone-container-padded flex h-full min-h-0 flex-col pt-safe-native-top-compact-md">
        {/* User Info Section */}
        <div className="flex items-center gap-3 pb-2 flex-shrink-0">
          <div className="relative">
            {user && (
              <input
                type="file"
                id="avatar-upload"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                disabled={isUploadingAvatar}
              />
            )}
            <label
              htmlFor={user ? "avatar-upload" : undefined}
              className={cn(
                'block w-18 h-18 shrink-0 overflow-hidden bg-muted rounded-[var(--radius)] relative cursor-pointer group shadow-sm',
                !user && 'pointer-events-none',
                isUploadingAvatar && 'pointer-events-none',
              )}
            >
              {avatarUrl ? (
                <>
                  <img 
                    src={avatarUrl as string} 
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                  {user && !isUploadingAvatar && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/40 rounded-[var(--radius)]">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-primary group-hover:bg-primary/90 transition-colors px-1">
                  {user ? (
                    <>
                      <Camera className="w-4 h-4 text-white mb-0.5" />
                      <span className="text-[10px] leading-tight text-white font-medium text-center">{t('profile.addPhoto')}</span>
                    </>
                  ) : (
                    <span className="text-white text-sm font-medium">
                      {getInitials()}
                    </span>
                  )}
                </div>
              )}
              {isUploadingAvatar && (
                <div
                  className="absolute inset-0 flex items-center justify-center rounded-[var(--radius)] bg-black/50 backdrop-blur-[1px]"
                  aria-busy="true"
                >
                  <Loader2
                    className="h-5 w-5 shrink-0 animate-spin text-white motion-reduce:animate-none"
                    aria-hidden
                  />
                  <span className="sr-only">Uploading profile picture</span>
                </div>
              )}
            </label>
          </div>
          
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate">{displayName}</h2>
            {userData?.email && (
              <p className="text-sm text-muted-foreground">{userData.email}</p>
            )}
          </div>
        </div>

        {/* Chats Section - Takes remaining space */}
        <ProfileChatList className="flex-1 min-h-0 pb-4" />
        
        {/* Collections Section */}
        <div className="flex-shrink-0 pb-4">
          <div className="flex items-center justify-between pb-2">
            <h3 className="text-lg font-semibold">{t('profile.collections')}</h3>
            {!collectionsLoading && hasMultiplePages && (
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentCollectionPage === 0}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                  {currentCollectionPage + 1} / {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentCollectionPage === totalPages - 1}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
          
          {collectionsLoading ? (
            <div className="text-center py-8">
              <p>{t('common.loading')}</p>
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <p className="text-muted-foreground">{t('profile.noCollections')}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setIsCreateCollectionOpen(true)}
              >
                {t('profile.createCollection')}
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-4 grid-cols-3 gap-3">
              {getPageItems().map((item: any) => {
                if ('isNewBox' in item) {
                  // Render new collection box
                  return (
                    <div 
                      key={item.id}
                      className="cursor-pointer"
                      onClick={() => setIsCreateCollectionOpen(true)}
                    >
                      <div className="aspect-square rounded-lg overflow-hidden border-2 border-dashed border-border/50 hover:bg-muted/50 transition-colors flex flex-col items-center justify-center">
                        <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm font-medium text-muted-foreground px-2 text-center">
                          {t('profile.createCollection')}
                        </p>
                      </div>
                    </div>
                  )
                } else if ('isEmpty' in item) {
                  // Render empty box
                  return (
                    <div key={item.id}>
                      <div className="aspect-square rounded-lg overflow-hidden border-2 border-dashed border-border/50 bg-background"></div>
                    </div>
                  )
                } else {
                  // Render collection
                  return (
                    <CollectionPreview
                      key={item.id}
                      collection={item}
                      onClick={() => handleCollectionClick(item.id)}
                    />
                  )
                }
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Create Collection Sheet */}
      <CreateCollectionSheet 
        isOpen={isCreateCollectionOpen}
        onOpenChange={setIsCreateCollectionOpen}
        onCollectionCreated={() => {
          // The useCollections hook will automatically refetch collections
          // when a new collection is created due to Firestore listeners
        }}
      />
    </div>

    <AuthModal 
      isOpen={showLoginModal && onProfileRoute}
      onOpenChange={handleLoginModalOpenChange}
      defaultMode={authModalMode}
      source="profile_page"
    />
  </>
  )
}
